/**
 * Token Refresh Hook
 *
 * Proactively refreshes access token 5 minutes before expiration.
 * Prevents sudden session expiry during user activity.
 *
 * Usage:
 * ```typescript
 * function App() {
 *   useTokenRefresh();
 *   return <Routes />;
 * }
 * ```
 */

import { useEffect } from 'react';
import { useStore } from 'react-redux';

import { logout, sessionRestoreEnded } from '../slices/authSlice';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../App/hooks';
import type { RootState } from '../../../App/store';
import { refreshSession } from '../services/refreshSession';
import { startAuthStorageSync } from '../services/authStorageSync';

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const MAX_REFRESH_ATTEMPTS = 3; // initial attempt + 2 retries after transient failures
const RETRY_DELAY_MS = 30 * 1000;
const SESSION_EXPIRED_TOAST_ID = 'auth-session-expired';

const showSessionExpiredToast = (): void => {
  toast.info('Sesja wygasła. Zaloguj się ponownie.', {
    toastId: SESSION_EXPIRED_TOAST_ID,
    position: 'bottom-right',
    autoClose: 3500,
    hideProgressBar: true,
  });
};

export const useTokenRefresh = (): void => {
  const store = useStore<RootState>();
  const tokenExpiresAt = useAppSelector((state) => state.auth.tokenExpiresAt);
  const refreshToken = useAppSelector((state) => state.auth.refreshToken);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const sessionRestoring = useAppSelector((state) => state.auth.sessionRestoring === true);

  // Follow sessions rotated or ended by other tabs; mounted once, independent of the timer
  useEffect(() => startAuthStorageSync(store), [store]);

  // Page load with an expired access token: one refresh restores the session, no retry loop.
  // Single flight shares the request under a StrictMode double mount; the cancelled flag makes
  // only the surviving effect act on the result.
  useEffect(() => {
    if (!sessionRestoring) {
      return;
    }

    let cancelled = false;
    void refreshSession(store).then((result) => {
      if (cancelled || result === 'success') {
        // success: tokensRefreshed already authenticated the store and the timers take over
        return;
      }
      if (result === 'unauthorized') {
        store.dispatch(logout());
        showSessionExpiredToast();
        return;
      }
      // transient or stale: stay logged out, keep the stored keys so the next load retries
      store.dispatch(sessionRestoreEnded());
    });

    return () => {
      cancelled = true;
    };
  }, [sessionRestoring, store]);

  useEffect(() => {
    // Only schedule refresh if user is authenticated
    if (!isAuthenticated || !tokenExpiresAt || !refreshToken) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const performRefresh = async (attempt: number): Promise<void> => {
      const result = await refreshSession(store);
      if (cancelled || result === 'success' || result === 'stale') {
        // On success the new tokenExpiresAt re-runs this effect and schedules the next refresh;
        // stale means the session changed during the call, so there is nothing to do
        return;
      }

      if (result === 'unauthorized') {
        store.dispatch(logout());
        showSessionExpiredToast();
        return;
      }

      // Transient failure: the session is kept, retry a bounded number of times
      if (attempt < MAX_REFRESH_ATTEMPTS) {
        timeoutId = setTimeout(() => {
          void performRefresh(attempt + 1);
        }, RETRY_DELAY_MS);
      }
    };

    const refreshDelay = tokenExpiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS;
    if (refreshDelay > 0) {
      timeoutId = setTimeout(() => {
        void performRefresh(1);
      }, refreshDelay);
    } else {
      // Inside the refresh window or already expired: try to refresh before giving up
      void performRefresh(1);
    }

    // Cleanup on unmount or when the tokens change
    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [tokenExpiresAt, refreshToken, isAuthenticated, store]);
};
